import React, { useMemo, useState } from "react";
import { useMutation, useQuery } from "urql";
import { Formik, Form } from "formik";
import { sortBy } from "lodash";
import { Link } from "wouter";
import * as Yup from "yup";

import { SearchListField, SubmitButton, TextField } from "../../../../components/src/form";
import { ReactTable } from "../../../../components/src/react-table/ReactTable";
import { createReactTableColumnHelper, ReactTableColumnDef } from "../../../../components/src/react-table/features";
import { DefaultColumnFilter } from "../../../../components/src/react-table/ReactTableFilters";

import { T_ACCOUNT_CHARTS, UPSERT_T_ACCOUNT_CHART } from "../../../../graphql/src/taccountchart2/queries";

import type { Party, UpsertTAccountChart, UpsertTAccountChartInput } from "../../types.generated";

const createSchema = Yup.object({
    clientId: Yup.string().required("Client is required"),
    name: Yup.string().trim().required("Name is required")
});

export function TAccountChartsPage(): React.ReactElement {
    const [{ fetching, error, data }, refetch] = useQuery<{ tAccountCharts2: UpsertTAccountChart[]; clients: Party[] }>({
        query: T_ACCOUNT_CHARTS
    });
    const [_, upsertTAccountChart] = useMutation(UPSERT_T_ACCOUNT_CHART);
    const [createError, setCreateError] = useState<string | null>(null);

    const tAccountCharts = useMemo(() => {
        if (data && data.tAccountCharts2) {
            return sortBy(
                data.tAccountCharts2,
                (tAccountChart) => tAccountChart.client?.name,
                "name",
                (tAccountChart) => -tAccountChart.version
            );
        }
        return [];
    }, [data]);

    const clients = useMemo(() => {
        if (data && data.clients) {
            return sortBy(data.clients, "name");
        }
        return [];
    }, [data]);

    const columns = useMemo<ReactTableColumnDef<UpsertTAccountChart>[]>(() => {
        const columnHelper = createReactTableColumnHelper<UpsertTAccountChart>();

        return [
            columnHelper.accessor((tAccountChart) => tAccountChart.client?.name, {
                id: "client",
                header: "Client",
                size: 200,
                filter: DefaultColumnFilter
            }),
            columnHelper.accessor("name", {
                header: "Name",
                cell: (cellProps) => (
                    <Link to={`/taccountcharts2/${cellProps.row.original._id}`}>{cellProps.getValue() || "(no name)"}</Link>
                ),
                size: 200,
                filter: DefaultColumnFilter
            }),
            columnHelper.accessor("version", {
                header: "Version",
                size: 75
            }),
            columnHelper.accessor("locked", {
                header: "Locked",
                cell: (cellProps) => (cellProps.getValue() ? "Yes" : "No"),
                size: 75
            }),
            columnHelper.accessor((tAccountChart) => tAccountChart.updateUserInfo?.name, {
                id: "updateUser",
                header: "Updated by",
                size: 150
            }),
            columnHelper.accessor("updateTimestamp", {
                header: "Update timestamp",
                size: 200
            })
        ];
    }, []);

    if (fetching) return <p>Loading</p>;
    if (error) return <p>error: {JSON.stringify(error, null, 2)}</p>;

    return (
        <div>
            <div className="flex flex-wrap -mx-2">
                <div className="w-full lg:w-2/3 px-2 mb-4">
                    <h4>T-account charts</h4>
                    <p>
                        <Link to="/taccountchartlogic2">T-account chart logic</Link>
                    </p>
                    <p>Number of t-account charts: {tAccountCharts.length}</p>
                    <ReactTable columns={columns} data={tAccountCharts} />
                </div>
                <div className="w-full lg:w-1/3 px-2 mb-4">
                    <h5>Create new t-account chart</h5>
                    <p>A new chart starts from the default fund t-account chart.</p>
                    <Formik
                        initialValues={{ clientId: "", name: "" }}
                        validationSchema={createSchema}
                        onSubmit={async (values, { resetForm }) => {
                            setCreateError(null);
                            const input: UpsertTAccountChartInput = { clientId: values.clientId, name: values.name.trim() };
                            const result = await upsertTAccountChart({ input });
                            if (result.error) {
                                setCreateError(result.error.message);
                            } else {
                                resetForm();
                                refetch({ requestPolicy: "network-only" });
                            }
                        }}
                    >
                        {({ isSubmitting }) => (
                            <Form autoComplete="off">
                                <SearchListField name="clientId" label="Client" className="" disabled={false} options={clients} />
                                <TextField name="name" label="Name" className="" disabled={false} />
                                <SubmitButton disabled={isSubmitting} label="Create" />
                                {createError ? <div className="form-field-error">{createError}</div> : null}
                            </Form>
                        )}
                    </Formik>
                </div>
            </div>
        </div>
    );
}
